// Local product catalog — no Shopify.
// Edit / add products by editing the CATALOG array below.

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    productType?: string;
    tags?: string[];
    features?: string[];
    specifications?: Array<{ name: string; value: string }>;
    priceRange: {
      minVariantPrice: { amount: string; currencyCode: string };
    };
    compareAtPrice?: { amount: string; currencyCode: string };
    images: {
      edges: Array<{ node: { url: string; altText: string | null } }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: { amount: string; currencyCode: string };
          compareAtPrice?: { amount: string; currencyCode: string };
          availableForSale: boolean;
          selectedOptions: Array<{ name: string; value: string }>;
        };
      }>;
    };
    options: Array<{ name: string; values: string[] }>;
  };
}

interface ProductInput {
  handle: string;
  title: string;
  description: string;
  productType: string;
  tags: string[];
  price: number;
  compareAtPrice?: number;
  images: string[];
  features?: string[];
  specifications?: Array<{ name: string; value: string }>;
  variants?: Array<{ title: string; price?: number; compareAtPrice?: number }>;
  currencyCode?: string;
}

const CURRENCY = "PKR";

function makeProduct(p: ProductInput): ShopifyProduct {
  const currencyCode = p.currencyCode ?? CURRENCY;
  const variants = (p.variants && p.variants.length > 0
    ? p.variants
    : [{ title: "Default", price: p.price, compareAtPrice: p.compareAtPrice }]
  ).map((v, i) => ({
    node: {
      id: `${p.handle}::variant::${i}`,
      title: v.title,
      price: { amount: String(v.price ?? p.price), currencyCode },
      compareAtPrice:
        v.compareAtPrice ?? p.compareAtPrice
          ? {
              amount: String(v.compareAtPrice ?? p.compareAtPrice),
              currencyCode,
            }
          : undefined,
      availableForSale: true,
      selectedOptions: [{ name: "Option", value: v.title }],
    },
  }));

  return {
    node: {
      id: `local::${p.handle}`,
      title: p.title,
      description: p.description,
      handle: p.handle,
      productType: p.productType,
      tags: p.tags,
      features: p.features,
      specifications: p.specifications,
      priceRange: {
        minVariantPrice: { amount: String(p.price), currencyCode },
      },
      compareAtPrice: p.compareAtPrice
        ? { amount: String(p.compareAtPrice), currencyCode }
        : undefined,
      images: {
        edges: p.images.map((url) => ({
          node: { url, altText: p.title },
        })),
      },
      variants: { edges: variants },
      options:
        variants.length > 1
          ? [{ name: "Option", values: variants.map((v) => v.node.title) }]
          : [],
    },
  };
}

const KITCHEN = "Kitchen Essentials";
const HOME = "Home Gadgets";
const TOYS = "Kids Toys";

// -------- Product catalog (11 real products) --------
const CATALOG: ShopifyProduct[] = [
  makeProduct({
    handle: "16-in-1-vegetable-chopper",
    title: "16 in 1 Vegetable Chopper",
    productType: KITCHEN,
    tags: ["kitchen", "chopper", "bestseller"],
    price: 1999,
    compareAtPrice: 2999,
    description:
      "Save time in the kitchen with the ultimate 16-in-1 Vegetable Chopper. Slice, dice, grate, spiralize and julienne any vegetable in seconds with premium stainless-steel blades. A must-have for every Pakistani kitchen — safe, fast and effortless.",
    features: [
      "16 interchangeable stainless-steel blades",
      "Slice, dice, grate, spiralize & julienne",
      "Non-slip base for stable chopping",
      "Built-in storage compartment",
      "Dishwasher safe & BPA-free",
    ],
    specifications: [
      { name: "Material", value: "ABS Plastic + Stainless Steel" },
      { name: "Blades", value: "16 attachments" },
      { name: "Container Capacity", value: "1.2 Litre" },
      { name: "Color", value: "White / Green" },
    ],
    images: [
      "https://i.postimg.cc/4dj3yzGF/1-1781782759-6a33d8e74f169.jpg",
      "https://i.postimg.cc/Hn7V963g/3-1781782759-6a33d8e7c5e68.jpg",
      "https://i.postimg.cc/gkVv90qW/4-1781782759-6a33d8e7d5c33.jpg",
    ],
  }),
  makeProduct({
    handle: "mop-wet-and-dry",
    title: "Mop Wet & Dry",
    productType: HOME,
    tags: ["cleaning", "home", "mop"],
    price: 1500,
    compareAtPrice: 2000,
    description:
      "Deep-clean every surface effortlessly with our Wet & Dry Mop. A 360° rotating head, absorbent microfiber pad and press-to-drain bucket make cleaning tiled floors, wood and marble easier than ever.",
    features: [
      "360° rotating microfiber head",
      "Wet & dry cleaning modes",
      "Self-wringing telescopic handle",
      "Removable, machine-washable pads",
      "Ideal for tile, wood and marble",
    ],
    specifications: [
      { name: "Handle Length", value: "Adjustable 90–120 cm" },
      { name: "Pad Material", value: "Microfiber" },
      { name: "Rotation", value: "360°" },
    ],
    images: [
      "https://i.postimg.cc/fTPhNwwX/2-1776874595-69e8f4633b27c.jpg",
      "https://i.postimg.cc/rstNRCrW/4-1776874595-69e8f4637b641.jpg",
      "https://i.postimg.cc/Kcddq0QT/1-1776874594-69e8f462e3c0d.jpg",
    ],
  }),
  makeProduct({
    handle: "gas-compressor",
    title: "Gas Compressor",
    productType: HOME,
    tags: ["gas", "kitchen", "home"],
    price: 2400,
    compareAtPrice: 3000,
    description:
      "Boost your gas pressure and cook faster during peak hours with our silent Gas Compressor. Reliable, safe and easy to install — a lifesaver for Pakistani homes during winter.",
    features: [
      "Boosts low gas pressure instantly",
      "Silent, low-power motor",
      "Safe pressure regulator",
      "Compact & easy to install",
      "Suitable for LPG & Sui gas lines",
    ],
    specifications: [
      { name: "Power", value: "AC 220V" },
      { name: "Warranty", value: "6 Months" },
      { name: "Suitable For", value: "Domestic Kitchens" },
    ],
    images: [
      "https://i.postimg.cc/W1QFhL7W/10-1776350875-69e0f69b35df8.jpg",
      "https://i.postimg.cc/T2z3VqbM/Untitled-1776350875-69e0f69b49a25.jpg",
      "https://i.postimg.cc/3RwThWDb/9-1776350875-69e0f69b473ab.jpg",
    ],
  }),
  makeProduct({
    handle: "talk-toy",
    title: "Talk Toy",
    productType: TOYS,
    tags: ["toy", "kids", "gift", "interactive"],
    price: 1499,
    compareAtPrice: 2000,
    description:
      "The interactive Talk Toy repeats every word your child says in a funny voice, encouraging speech, laughter and imagination. Perfect birthday gift for kids aged 3+.",
    features: [
      "Repeats what you say in a funny voice",
      "Soft, huggable body",
      "Blinking eyes & wagging tail",
      "Encourages speech development",
      "USB rechargeable",
    ],
    specifications: [
      { name: "Age Group", value: "3+" },
      { name: "Battery", value: "USB Rechargeable" },
      { name: "Material", value: "Soft plush + ABS" },
    ],
    images: [
      "https://i.postimg.cc/1347y6Wg/24-1775144730-69ce8f1a6f02b.jpg",
      "https://i.postimg.cc/6q9zgG2K/28-1775144628-69ce8eb4623b4.jpg",
      "https://i.postimg.cc/BQnMCHGG/29-1775144628-69ce8eb469d57.jpg",
      "https://i.postimg.cc/vZphMKxM/S84010ef285254405a8c5ca831b1c6a916.webp",
    ],
  }),
  makeProduct({
    handle: "3-in-1-bowl-set",
    title: "3 in 1 Bowl Set",
    productType: KITCHEN,
    tags: ["kitchen", "bowl", "serving"],
    price: 1600,
    compareAtPrice: 2100,
    description:
      "A stylish 3-in-1 Bowl Set that serves salads, soups and snacks in elegance. Made from premium food-grade material, perfect for daily use and dinner parties.",
    features: [
      "Set of 3 stackable bowls",
      "Food-grade, BPA-free material",
      "Elegant serving design",
      "Microwave & dishwasher safe",
      "Space-saving stackable storage",
    ],
    specifications: [
      { name: "Pieces", value: "3 Bowls" },
      { name: "Material", value: "Food-grade Plastic" },
      { name: "Microwave Safe", value: "Yes" },
    ],
    images: [
      "https://i.postimg.cc/zGmHfY7P/126-1778933543-6a085f27dea50-1778940401-6a0879f1a9d3f.jpg",
      "https://i.postimg.cc/52MSjRSP/131-1778933544-6a085f286ded5.jpg",
      "https://i.postimg.cc/2yZnSQqv/128-1778933544-6a085f2821f52.jpg",
      "https://i.postimg.cc/Vk1XzbzD/132-1778933544-6a085f28827d1.jpg",
    ],
  }),
  makeProduct({
    handle: "electric-kettle",
    title: "Electric Kettle",
    productType: KITCHEN,
    tags: ["kitchen", "kettle", "appliance"],
    price: 1799,
    compareAtPrice: 2500,
    description:
      "Boil water in under 3 minutes with our 1500W fast-boil Electric Kettle. Featuring auto shut-off, boil-dry protection and a sleek stainless-steel body — the perfect chai companion.",
    features: [
      "1500W fast-boil heating element",
      "Auto shut-off & boil-dry protection",
      "1.8L capacity — 8 cups",
      "Cool-touch handle & 360° base",
      "Removable washable filter",
    ],
    specifications: [
      { name: "Capacity", value: "1.8 Litre" },
      { name: "Power", value: "1500W, 220V" },
      { name: "Body", value: "Stainless Steel" },
    ],
    images: [
      "https://i.postimg.cc/0ybPMV5R/main-image-4-6930a93146fb4-1773877976-69bb3ad8b27ce.jpg",
      "https://i.postimg.cc/mgvfcR6P/2-6953d9e25286b-1773877976-69bb3ad8b9341.jpg",
      "https://i.postimg.cc/kG1L7dbs/4-6953d9e253689-1773877976-69bb3ad8c40eb.jpg",
    ],
  }),
  makeProduct({
    handle: "electric-mosquito-device",
    title: "Electric Mosquito Device",
    productType: HOME,
    tags: ["home", "mosquito", "insect"],
    price: 1300,
    compareAtPrice: 1800,
    description:
      "Chemical-free, silent and safe — our Electric Mosquito Device attracts and traps mosquitos through UV light and vacuum action. Enjoy peaceful, bite-free nights.",
    features: [
      "Silent UV-light mosquito trap",
      "Chemical-free & child-safe",
      "USB powered — plug anywhere",
      "Ideal for bedrooms & lounges",
      "Easy-clean collection tray",
    ],
    specifications: [
      { name: "Power", value: "USB 5V" },
      { name: "Coverage", value: "Up to 30 sq m" },
      { name: "Noise Level", value: "Silent" },
    ],
    images: [
      "https://i.postimg.cc/zX4P77S8/4-1774612196-69c66ee4323e4.jpg",
      "https://i.postimg.cc/1tRWXjPc/1-1774612195-69c66ee3df515.jpg",
      "https://i.postimg.cc/1RjWRBdG/5-1774612196-69c66ee4373cf.jpg",
      "https://i.postimg.cc/pLDkZHLC/2-1774612196-69c66ee412c8e.jpg",
    ],
  }),
  makeProduct({
    handle: "milk-bottle-warmer",
    title: "Milk Bottle Warmer",
    productType: HOME,
    tags: ["baby", "kitchen", "warmer"],
    price: 1499,
    compareAtPrice: 2000,
    description:
      "Warm your baby's milk to the perfect temperature in minutes. Our Milk Bottle Warmer keeps nutrients intact while ensuring safety with even, gentle heating — essential for every parent.",
    features: [
      "Fast, even warming for baby bottles",
      "Preserves milk's nutritional value",
      "Fits all standard bottle sizes",
      "Auto temperature control",
      "Compact bedside design",
    ],
    specifications: [
      { name: "Power", value: "80W, 220V" },
      { name: "Warming Time", value: "3–5 minutes" },
      { name: "Fits", value: "All standard baby bottles" },
    ],
    images: [
      "https://i.postimg.cc/qBhF3Wyc/download-1780904483-6a267223e9b23.jpg",
      "https://i.postimg.cc/xTLFsHsL/download2-1780904485-6a26722509a35.jpg",
      "https://i.postimg.cc/hGZCJ0G2/download3-1780904485-6a2672252b5e2.jpg",
      "https://i.postimg.cc/ydJL0SgZ/images-1780904485-6a2672252eb5e.jpg",
    ],
  }),
  makeProduct({
    handle: "portable-air-conditioner-fan",
    title: "Portable Air Conditioner Fan",
    productType: HOME,
    tags: ["fan", "cooling", "summer", "home"],
    price: 2500,
    compareAtPrice: 3000,
    description:
      "Stay cool anywhere with our Portable Air Conditioner Fan. Rechargeable, ultra-quiet and packed with a powerful mist cooling function — ideal for load-shedding, offices and travel.",
    features: [
      "3-in-1 fan, humidifier & air cooler",
      "Rechargeable long-lasting battery",
      "Adjustable 3-speed airflow",
      "Whisper-quiet operation",
      "USB-C charging",
    ],
    specifications: [
      { name: "Battery", value: "Rechargeable Li-Ion" },
      { name: "Water Tank", value: "500 ml" },
      { name: "Modes", value: "3 speeds + mist" },
    ],
    images: [
      "https://i.postimg.cc/yxH0WJFV/1763200037-69184c25a8a11-1000058781-1773785596-69b9d1fcd8839.jpg",
      "https://i.postimg.cc/8kRh9HSc/1763200038-69184c26213e2-1000058782-1773785597-69b9d1fd001d0.jpg",
      "https://i.postimg.cc/qMp8T3Jm/1763200038-69184c26629de-1000058783-1773785597-69b9d1fd04a72.jpg",
    ],
  }),
  makeProduct({
    handle: "safe-slicer-vegetable-cutter",
    title: "Safe Slicer Vegetable Cutter",
    productType: KITCHEN,
    tags: ["kitchen", "slicer", "cutter"],
    price: 1800,
    compareAtPrice: 2300,
    description:
      "Slice vegetables safely and evenly in seconds with our Safe Slicer. Finger-guard, adjustable thickness and razor-sharp stainless-steel blades make prep work fast and effortless.",
    features: [
      "Adjustable thickness slicer",
      "Finger-guard for safe slicing",
      "Razor-sharp stainless-steel blade",
      "Non-slip base",
      "Easy to clean & store",
    ],
    specifications: [
      { name: "Blade", value: "Stainless Steel" },
      { name: "Thickness Levels", value: "5 settings" },
    ],
    images: [
      "https://i.postimg.cc/MGBcR6Zf/5-1778859318-6a073d3626159.jpg",
      "https://i.postimg.cc/sxj1t5Y5/1-1778859317-6a073d35a10fe.jpg",
      "https://i.postimg.cc/Df6S1Yh5/6-1778859318-6a073d362df87.jpg",
    ],
  }),
  makeProduct({
    handle: "water-bottle",
    title: "Stainless Steel Vacuum Water Bottle",
    productType: KITCHEN,
    tags: ["water bottle", "vacuum", "hot", "cold"],
    price: 2000,
    compareAtPrice: 2500,
    description:
      "Keep drinks hot for 12 hours and cold for 24 with our premium double-wall vacuum-insulated Water Bottle. Leak-proof flip-top cap and durable stainless-steel body — perfect for office, gym and travel.",
    features: [
      "Double-wall vacuum insulation",
      "Hot for 12 hrs • Cold for 24 hrs",
      "Leak-proof flip-top lid",
      "Food-grade stainless steel",
      "BPA-free & rust resistant",
    ],
    specifications: [
      { name: "Capacity", value: "900 ml" },
      { name: "Material", value: "18/8 Stainless Steel" },
      { name: "Insulation", value: "Double-wall Vacuum" },
    ],
    images: [
      "https://i.postimg.cc/DymwLv59/sm-flip-top-900-ml-bottle-stainless-steel-vacuum-bottle-double-wall-insulated-water-bottle-hot-and-c.jpg",
    ],
  }),
];

// -------- Public API --------

export async function fetchProducts(
  query?: string,
  first = 50,
): Promise<ShopifyProduct[]> {
  let list = CATALOG;
  if (query && query.trim()) {
    const term = query.toLowerCase().trim();
    list = list.filter(
      (p) =>
        p.node.title.toLowerCase().includes(term) ||
        p.node.description.toLowerCase().includes(term) ||
        p.node.productType?.toLowerCase().includes(term) ||
        p.node.tags?.some((t) => t.toLowerCase().includes(term)),
    );
  }
  return list.slice(0, first);
}

export async function fetchProductByHandle(
  handle: string,
): Promise<ShopifyProduct["node"] | null> {
  const p = CATALOG.find((x) => x.node.handle === handle);
  return p ? p.node : null;
}

export async function fetchRelatedProducts(
  handle: string,
  productType?: string,
  limit = 4,
): Promise<ShopifyProduct[]> {
  const others = CATALOG.filter((x) => x.node.handle !== handle);
  const sameType = productType
    ? others.filter((x) => x.node.productType === productType)
    : [];
  const rest = others.filter((x) => !sameType.includes(x));
  return [...sameType, ...rest].slice(0, limit);
}

export function formatPrice(amount: string | number, currencyCode = CURRENCY) {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  return `Rs.${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function calcDiscountPercent(
  price: string | number,
  compareAt?: string | number,
): number {
  if (!compareAt) return 0;
  const p = typeof price === "string" ? parseFloat(price) : price;
  const c = typeof compareAt === "string" ? parseFloat(compareAt) : compareAt;
  if (!c || c <= p) return 0;
  return Math.round(((c - p) / c) * 100);
}
